import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    Popper,
    Paper,
    Slide,
    Stack,
    Typography,
} from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import useFilter from "@hooks/useFilter";
import { IconResetSmall, IconMoreHoriz } from "@mzc-cloudplex/icons";
import FilterChipExpand from "../list-search/filter-chip-expand";
import { ASSET_MEIDA_TYPES, SELECT_STATUS, TABLE_LIST_TYPE } from "@constants";
import ChipTypesInput from "../chip-popper-components/chip-types-input/chip-types-input";
import { BasicDateTimePicker } from "@features/datepicker/dateTimepicker";
import moment from "moment";
import { loadViewInfo, saveViewInfo } from "@cores/viewStorage";
import { useSelector } from "react-redux";
import TableSort from "@components_v2/table-sort/table-sort";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { alpha } from "@mui/material";

const LEFT_GRID_WIDTH = 240; // 왼쪽 Grid의 고정 너비 (px)
const BUTTON_CONTAINER_MARGIN = 40; // 버튼 컨테이너 여백

const ListToolsWrapper = ({ mode, isCollapsed, children, hiddenDivider }) => {
    if (mode === "collapse") {
        return (
            <Collapse
                in={isCollapsed}
                collapsedSize={1}
                sx={{
                    backgroundColor: theme.palette.greySecondary.main,
                    color: theme.palette.common.white,
                    borderRadius: `3px`,
                }}
            >
                {children}
            </Collapse>
        );
    } else {
        return (
            <>
                {!hiddenDivider && (
                    <Box>
                        <Divider
                            flexItem
                            orientation={"horizontal"}
                            sx={{ borderColor: theme.palette.greySecondary.main }}
                        />
                    </Box>
                )}
                <Box
                    sx={{
                        position: "fixed",
                        bottom: theme.spacing(5),
                        left: "50%",
                        transform: "translateX(-50%)",
                        maxWidth: "70%",
                        zIndex: 400,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    <Slide
                        direction="up"
                        in={isCollapsed}
                        mountOnEnter
                        unmountOnExit
                        sx={{
                            backgroundColor: theme.palette.greySecondary.main,
                            color: theme.palette.common.white,
                            borderRadius: theme.spacing(1),
                            p: theme.spacing(1.5),
                        }}
                    >
                        {children}
                    </Slide>
                </Box>
            </>
        );
    }
};

const ListToolCollapse = ({
    type,
    sortOptions,
    isCollapsed,
    toolSize,
    numSelected,
    pageCount,
    getButtons,
    getPopperButtons = () => [],
    pagination,
    filter,
    viewToggleButtons,
    hideCheckbox,
    handleClearCheckAll,
    handleChangeCheckAll,
    filterDisabled,
    totalCount,
    totalView,
    onChangeSort,
    onChangeSortAsc,
    mode = "slide",
    hiddenDivider,
}) => {
    const { t } = useTranslation();

    const spaceId = useSelector(({ stage }) => stage?.id);
    const projectId = useSelector(({ project }) => project?.id);

    const [resizedButtons, setResizedButtons] = useState([]);
    const [displayPopperButtons, setDisplayPopperButtons] = useState([]);
    const [hideText, setHideText] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [value, setValue] = React.useState([]);
    const [pendingValue, setPendingValue] = React.useState([]);
    const [selectedSortIndex, setSelectedSortIndex] = React.useState(null);
    const [isSortAsc, setIsSortAsc] = useState(false);
    const [fullSizeStackWidth, setFullSizeStackWidth] = useState(0);
    const [showHiddenStack, setShowHiddenStack] = useState(false);
    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const moreOpen = Boolean(moreAnchorEl);
    const hiddenStackRef = useRef(null);
    const ref = useRef(null);
    const gridRef = useRef(null);
    const stackRef = useRef(null);
    const containerRef = useRef(null);
    const moreButtonRef = useRef(null);

    const {
        filterValue,
        editFilterValue,
        updateEditFilter,
        resetEditFilter,
        saveFilter,
        deleteFilter,
        onUpdateFilterParams,
    } = useFilter(type);

    const handleMoreClick = (event) => {
        setMoreAnchorEl(moreAnchorEl ? null : event.currentTarget);
    };

    const handleMoreClose = () => {
        setMoreAnchorEl(null);
    };

    const onHandleDeleteFilter = useCallback(
        (id) => {
            deleteFilter(id, (filterParams) => {
                onUpdateFilterParams(filterParams);
            });
        },
        [onUpdateFilterParams],
    );

    const handleClose = () => {
        setValue(pendingValue);
        if (anchorEl) {
            anchorEl.focus();
        }
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "search-filter-open" : undefined;

    const handleMenuItemClick = (index) => {
        setSelectedSortIndex(index);
        onChangeSort(sortOptions[index].id);

        const viewInfo = getViewInfoByType(spaceId, projectId, type);
        if (viewInfo)
            saveViewInfo("assetsViewInfo", {
                ...viewInfo,
                sortBy: sortOptions[index].id,
            });
    };

    const handleChangeSortAsc = (value) => {
        setIsSortAsc(value);
        onChangeSortAsc(value);
        const viewInfo = getViewInfoByType(spaceId, projectId, type);
        if (viewInfo)
            saveViewInfo("assetsViewInfo", {
                ...viewInfo,
                isSortAsc: value,
            });
    };

    const getSelectComponent = useCallback(
        ({ id, options, ...params }) => {
            if (!id) return;
            if (id === "mediaType")
                return (
                    <ChipTypesInput
                        types={editFilterValue.mediaType}
                        placeholder={t("common::label::Select media type", "Select media type")}
                        options={Object.values(ASSET_MEIDA_TYPES).map((type) => ({
                            title: t(`common::label::${type}`, type),
                            id: type,
                        }))}
                        onDeleteType={(index) => {
                            updateEditFilter(
                                "mediaType",
                                editFilterValue.mediaType.filter((t, idx) => idx !== index),
                            );
                        }}
                        onAddType={(ext) => {
                            updateEditFilter(
                                "mediaType",
                                editFilterValue.mediaType ? [...editFilterValue.mediaType, ext] : [ext],
                            );
                        }}
                    />
                );
            if (id === "status") {
                return (
                    <ChipTypesInput
                        types={editFilterValue.status}
                        placeholder={t("common::label::Select status")}
                        options={Object.entries(SELECT_STATUS[type]).map(([key, value]) => ({
                            title: t(`common::label::${value}`),
                            id: key,
                        }))}
                        onDeleteType={(index) => {
                            updateEditFilter(
                                "status",
                                editFilterValue.status.filter((t, idx) => idx !== index),
                            );
                        }}
                        onAddType={(ext) => {
                            updateEditFilter(
                                "status",
                                editFilterValue.status ? [...editFilterValue.status, ext] : [ext],
                            );
                        }}
                    />
                );
            }
            if (params.type === "dateTimeRange") {
                return (
                    <Stack direction={"row"} gap={0.5} alignItems={"center"} width={"100%"}>
                        <BasicDateTimePicker
                            label={t("common::label::Start")}
                            value={editFilterValue[id]?.start && moment(editFilterValue[id]?.start)}
                            onChangeValue={(value) => {
                                updateEditFilter(id, {
                                    ...editFilterValue?.[id],
                                    start: value.toISOString(),
                                });
                            }}
                        />
                        <Typography variant={"subtitle2_700"}>~</Typography>
                        <BasicDateTimePicker
                            label={t("common::label::End")}
                            value={editFilterValue[id]?.end && moment(editFilterValue[id]?.end)}
                            onChangeValue={(value) => {
                                updateEditFilter(id, {
                                    ...editFilterValue?.[id],
                                    end: value.toISOString(),
                                });
                            }}
                        />
                    </Stack>
                );
            }
            return (
                <ChipTypesInput
                    types={editFilterValue?.[id]}
                    placeholder={params.placeholder ?? t(`common::label::Select ${id}`)}
                    options={options}
                    onDeleteType={(index) => {
                        updateEditFilter(
                            id,
                            editFilterValue?.[id].filter((t, idx) => idx !== index),
                        );
                    }}
                    onAddType={(ext) => {
                        updateEditFilter(id, editFilterValue?.[id] ? [...editFilterValue?.[id], ext] : [ext]);
                    }}
                />
            );
        },
        [type, editFilterValue],
    );

    const onHandleSaveFilter = useCallback(
        (item) => {
            const { id, type } = item;
            if (["multiSelect", "user", "project"].includes(type) && editFilterValue[id]?.length === 0) {
                onHandleDeleteFilter(id);
            } else {
                saveFilter(id, type, (filterParams) => {
                    onUpdateFilterParams(filterParams);
                });
            }
        },
        [onUpdateFilterParams, saveFilter],
    );

    const filterParams = useMemo(() => {
        if (!filter) return;
        const newFilterList = filter?.map((item) => {
            const data = filterValue[item.id];
            if (typeof data === "object" && data?.length > 0)
                return {
                    ...item,
                    label: data?.length > 0 ? data[0].label : null,
                    value: data,
                    length: data?.length > 1 ? data?.length - 1 : null,
                };
            else if (item.type === "dateTimeRange")
                return {
                    ...item,
                    label: `${moment(data?.start)?.format("YYYY-MM-DD HH:mm:ss")} ~ ${moment(data?.end)?.format(
                        "YYYY-MM-DD HH:mm:ss",
                    )}`,
                    value: data,
                    length: null,
                };

            return { ...item, label: data, value: data, length: null };
        });
        return newFilterList;
    }, [filter, filterValue]);

    useEffect(() => {
        setShowHiddenStack(true);
    }, [getButtons, numSelected]);

    useEffect(() => {
        if (showHiddenStack && hiddenStackRef.current) {
            setFullSizeStackWidth(hiddenStackRef.current.scrollWidth);
            setShowHiddenStack(false);
        }
    }, [showHiddenStack]);

    useEffect(() => {
        const handleResize = () => {
            if (!containerRef?.current) return;
            const containerWidth = containerRef.current.clientWidth;
            const availableWidth = containerWidth - LEFT_GRID_WIDTH - BUTTON_CONTAINER_MARGIN;

            const popperButtonsArray = getPopperButtons() || [];

            const visiblePopperButtons = popperButtonsArray
                .map((buttons) => buttons.filter((button) => !button.props.hidden))
                .filter((buttons) => buttons.length > 0);

            if (fullSizeStackWidth > availableWidth) {
                setResizedButtons(getButtons(true));
                setHideText(true);
                setDisplayPopperButtons(visiblePopperButtons);
            } else {
                setResizedButtons(getButtons(false));
                setHideText(false);
                setDisplayPopperButtons(visiblePopperButtons);
            }
        };

        handleResize();

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [fullSizeStackWidth, getButtons, getPopperButtons]);

    useEffect(() => {
        const viewInfo = getViewInfoByType(spaceId, projectId, type);
        if (viewInfo) {
            if (viewInfo?.sortBy) {
                const index = sortOptions.findIndex((option) => option.id === viewInfo.sortBy);
                setSelectedSortIndex(index);
                onChangeSort(sortOptions[index].id);
            }
            if (viewInfo.isSortAsc) {
                setIsSortAsc(viewInfo.isSortAsc);
            }
        }
    }, [spaceId, projectId]);

    const getViewInfoByType = (spaceId, projectId, domain) => {
        // NOTE: domain별로 sort를 관리하기 위해 domain을 받아서 key를 반환하는 함수
        if (!domain || !spaceId || !projectId) return undefined;
        const getViewInfoKey = (domain) => {
            switch (domain) {
                case TABLE_LIST_TYPE.ASSET:
                    return "assetsViewInfo";
                case TABLE_LIST_TYPE.COLLECTION:
                    return "collectionsViewInfo";
                case TABLE_LIST_TYPE.MUSIC:
                    return "musicViewInfo";
                case TABLE_LIST_TYPE.PHOTO:
                    return "photosViewInfo";
                case TABLE_LIST_TYPE.VIDEO:
                    return "videosViewInfo";
                case TABLE_LIST_TYPE.CMS_VIDEO:
                    return "cmsVideosViewInfo";
                case TABLE_LIST_TYPE.REQUEST:
                    return "requestsViewInfo";
                default:
                    return undefined;
            }
        };
        const viewInfoKey = getViewInfoKey(domain);
        return viewInfoKey ? loadViewInfo(viewInfoKey, spaceId, projectId) : null;
    };

    return (
        <>
            {/* 임시로 숨겨진 Stack을 렌더링하여 크기 측정 후 바로 제거 */}
            {showHiddenStack && (
                <div
                    style={{
                        position: "absolute",
                        left: -9999,
                        visibility: "hidden",
                        height: 0,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "row",
                    }}
                    ref={hiddenStackRef}
                >
                    <Stack direction={"row"} gap={1.5}>
                        {getButtons(false).map((button, idx) => (
                            <Fragment key={idx}>{button}</Fragment>
                        ))}
                    </Stack>
                    {getPopperButtons().length > 0 && (
                        <>
                            <Divider
                                flexItem
                                orientation={"vertical"}
                                sx={{ my: 0.5, ml: theme.spacing(1.5), opacity: 0.15 }}
                                color={theme.palette.greySecondary[100]}
                            />
                            <IconButton circled size={"medium"} color={"inherit"}>
                                <IconMoreHoriz size={16} htmlColor={theme.palette.common.white} />
                            </IconButton>
                        </>
                    )}
                </div>
            )}
            <ListToolsWrapper mode={mode} isCollapsed={isCollapsed} hiddenDivider={hiddenDivider}>
                <Grid
                    container
                    flexDirection={toolSize === "sm" ? `column` : `row`}
                    alignItems={`center`}
                    justifyContent={`space-between`}
                    sx={{
                        backgroundColor: theme.palette.greySecondary.main,
                        color: theme.palette.common.white,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.greySecondary.main}`,
                    }}
                    p={1}
                    ref={containerRef}
                >
                    <Grid item sx={{ flex: 0, flexBasis: `${LEFT_GRID_WIDTH}px` }}>
                        <Grid container alignItems={"center"}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color={"greySecondary"}
                                        checked={numSelected ? numSelected > 0 : false}
                                        indeterminate={numSelected > 0 && numSelected !== pageCount}
                                        hidden={hideCheckbox}
                                    />
                                }
                                label={
                                    <>
                                        <Typography variant={"body3_700"}>{`${numSelected} `}</Typography>
                                        {!hideText && (
                                            <Typography variant={"body3_400"}>
                                                {t("common::label::item selected", "item selected")}
                                            </Typography>
                                        )}
                                    </>
                                }
                                onClick={handleChangeCheckAll}
                                sx={{ ml: 0 }}
                            />
                            <Divider flexItem orientation={"vertical"} sx={{ my: 0.5, mr: 1.5, opacity: 0.12 }} />
                            {hideText ? (
                                <IconButton size={"xsmall"} onClick={handleClearCheckAll}>
                                    <IconResetSmall size={16} htmlColor={theme.palette.common.white} />
                                </IconButton>
                            ) : (
                                <Button
                                    size={"small"}
                                    variant={"text"}
                                    endIcon={<IconResetSmall size={16} htmlColor={theme.palette.common.white} />}
                                    onClick={handleClearCheckAll}
                                >
                                    {!hideText ? (
                                        <Typography variant={"body1Regular"} color={theme.palette.common.white}>
                                            {t("common::label::Clear")}
                                        </Typography>
                                    ) : undefined}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        sx={{ flex: 1, display: "flex", justifyContent: "flex-end", overflow: "auto" }}
                        ref={gridRef}
                    >
                        <Stack
                            direction={"row"}
                            gap={1.5}
                            ref={stackRef}
                            sx={{
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                                minWidth: "min-content",
                                alignItems: "center",
                            }}
                        >
                            {resizedButtons.map((button, index) => (
                                <Fragment key={`list-tools-buttons-${index}`}>{button}</Fragment>
                            ))}

                            {/* 더보기 버튼 및 Popper */}
                            {displayPopperButtons.length > 0 && (
                                <>
                                    <Divider
                                        flexItem
                                        orientation={"vertical"}
                                        sx={{ my: 0.5, ml: theme.spacing(1.5), opacity: 0.15 }}
                                        color={theme.palette.greySecondary[100]}
                                    />
                                    <IconButton
                                        circled
                                        size={"medium"}
                                        onClick={handleMoreClick}
                                        ref={moreButtonRef}
                                        color={"inherit"}
                                    >
                                        <IconMoreHoriz size={16} htmlColor={theme.palette.common.white} />
                                    </IconButton>

                                    <Popper
                                        open={moreOpen}
                                        anchorEl={moreAnchorEl}
                                        placement="bottom-start"
                                        sx={{ zIndex: 1300 }}
                                    >
                                        <ClickAwayListener onClickAway={handleMoreClose}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    minWidth: 160,
                                                    border: `1px solid ${theme.palette.greySecondary[100]}`,
                                                    borderRadius: theme.spacing(0.375),
                                                    boxShadow: `0px 2px 4px 0px ${alpha(
                                                        theme.palette.constant.white,
                                                        0.12,
                                                    )}`,
                                                }}
                                            >
                                                <List onClick={() => handleMoreClose()}>
                                                    {displayPopperButtons.map((el, index) => {
                                                        return (
                                                            <>
                                                                {el?.map((button, index) => {
                                                                    return (
                                                                        <ListItem
                                                                            key={`popper-button-${index}`}
                                                                            disablePadding
                                                                            sx={{
                                                                                "& .MuiListItemButton-root": {
                                                                                    py: "1px",
                                                                                    px: theme.spacing(1.75),
                                                                                },
                                                                            }}
                                                                        >
                                                                            {button}
                                                                        </ListItem>
                                                                    );
                                                                })}
                                                                {displayPopperButtons.length - 1 > index && <Divider />}
                                                            </>
                                                        );
                                                    })}
                                                </List>
                                            </Paper>
                                        </ClickAwayListener>
                                    </Popper>
                                </>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </ListToolsWrapper>
            <Box>
                <Grid container alignItems={`center`} justifyContent={`space-between`} py={1.5}>
                    {totalView && (
                        <Grid item flex={1}>
                            <Typography variant={"body1_700"}>{t("common::label::Total")}</Typography>
                            <Typography>{` ${totalCount}`}</Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Grid container alignItems={`center`} gap={1.5}>
                            {handleChangeCheckAll && (
                                <Grid item>
                                    <Collapse in={mode === "slide" ? true : numSelected === 0} orientation="horizontal">
                                        <Checkbox
                                            color={"greySecondary"}
                                            checked={numSelected >= 1}
                                            indeterminate={numSelected >= 1 && numSelected !== pageCount}
                                            onClick={handleChangeCheckAll}
                                            hidden={hideCheckbox || getButtons(false)?.length === 0}
                                        />
                                    </Collapse>
                                </Grid>
                            )}
                            {sortOptions && (
                                <Grid item>
                                    <TableSort
                                        sortOptions={sortOptions}
                                        selectedIndex={selectedSortIndex}
                                        isSortAsc={isSortAsc}
                                        onChangeSort={handleMenuItemClick}
                                        onChangeSortAsc={handleChangeSortAsc}
                                    />
                                </Grid>
                            )}
                            {sortOptions && filterParams?.length > 0 && (
                                <Divider flexItem orientation={"vertical"} sx={{ my: 0.5, mx: 1.5 }} />
                            )}
                            {!filterDisabled && (
                                <Grid item>
                                    <Grid container flexDirection={"row"} alignItems={"center"} gap={1.5}>
                                        {filterParams?.map((item, index) => {
                                            return (
                                                <FilterChipExpand
                                                    key={`filter-chip-expand-${index}`}
                                                    item={item}
                                                    onDelete={() => onHandleDeleteFilter(item.id)}
                                                    isDisabled={editFilterValue[item.id] === filterValue[item.id]}
                                                    onCancel={() => resetEditFilter()}
                                                    onSave={() => onHandleSaveFilter(item)}
                                                    component={getSelectComponent(item)}
                                                    sx={{
                                                        width: item.type === "dateTimeRange" ? "500px" : "360px",
                                                    }}
                                                />
                                            );
                                        })}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item sx={{ display: "flex", flexDirection: "row", alignItems: "center", pr: 2 }}>
                        <Grid item>
                            <Grid container flexWrap={"nowrap"} alignItems={"center"}>
                                {pagination}
                            </Grid>
                        </Grid>
                        {viewToggleButtons}
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default ListToolCollapse;
